/**
 * Shell tab model — pure state, no React, no DOM.
 *
 * A tab is identified by `id` and located by `path` (pathname + search). Dedupe
 * is exact `path` equality against the tab's CURRENT path. `lastActiveAt` moves
 * only on focus (D4); navigating inside a tab never touches it.
 */
import type { CapabilityAction } from '../types/qa';
import { buildKwRequest, baseFilters } from '../pages/keywordRequest';
import { keywordSearchTerms } from '../utils/keywordQueryText';
import type { KeywordSearchGroup } from '../types';

export const TAB_CAP = 8;
const TITLE_MAX = 40;

export interface Tab { id: string; path: string; title: string; lastActiveAt: number }
export interface TabsState { tabs: Tab[]; activeTabId: string | null }
export type PendingOpen = {
  path: string; evictId: string; evictTitle: string; background: boolean;
};
export type OpenResult =
  | { kind: 'focused'; state: TabsState }
  | { kind: 'opened'; state: TabsState }
  | { kind: 'needsEviction'; pending: PendingOpen };

export function newTabId(): string {
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function parse(path: string): URL {
  try {
    return new URL(path || '/', 'http://shell.local');
  } catch {
    return new URL('/', 'http://shell.local');
  }
}

/** D7: Home is `/` with an empty query. */
export function isHomePath(path: string): boolean {
  const u = parse(path);
  return u.pathname === '/' && u.search === '';
}

/** R1.2a: the one tab path an alias stands for. `/subscriptions` (any query) -> `/account`. */
export function canonicalTabPath(path: string): string {
  return parse(path).pathname === '/subscriptions' ? '/account' : path;
}

function clip(s: string): string {
  return s.length > TITLE_MAX ? `${s.slice(0, TITLE_MAX - 1)}…` : s;
}

export function withDetail(label: string, detail: string | null | undefined): string {
  const d = (detail ?? '').trim();
  return d ? clip(`${label}: ${d}`) : label;
}

function firstKeyword(kwConfig: string | null): string | null {
  if (!kwConfig) return null;
  try {
    const cfg = JSON.parse(kwConfig) as {
      groups?: KeywordSearchGroup[];
      rows?: { keyword?: string }[];
    };
    const kw = cfg.groups?.[0]?.rows?.[0]?.keyword ?? cfg.rows?.[0]?.keyword;
    // A proximity-only search has no rows: name it by its proximity.
    return kw ?? keywordSearchTerms(cfg.groups?.slice(0, 1) ?? [])[0] ?? null;
  } catch {
    return null;
  }
}

export function titleForPath(path: string): string {
  const u = parse(path);
  const p = u.pathname;
  if (p === '/') {
    if (u.search === '') return 'Home';
    const q = u.searchParams;
    switch (q.get('mode')) {
      case 'ai': return withDetail('Cogni', q.get('q'));
      case 'researcher': return 'Investigator';
      case 'workstream': return 'Agentic reporting';
      default: return withDetail('Keyword', firstKeyword(q.get('kwConfig')));
    }
  }
  const seg = p.split('/').filter(Boolean);
  switch (seg[0]) {
    case 'drug': return seg[1] ? `Drug ${seg[1]}` : 'Drug';
    case 'generic': return 'Generic';
    case 'compare-keyword': return 'Compare';
    case 'manage-drafts': return 'Drafts';
    case 'account':
    case 'subscriptions': return 'Your account';
    case 'research': return 'Investigator';
    case 'cohort': return 'Cohort';
    case 'training': return 'Training';
    case 'workstream': return seg[2] === 'report' ? 'Report' : 'Workstream';
    case 'agents': return 'Agentic reporting';
    case 'sba': return 'SBA document';
    case 'fda-letter': return 'FDA letter';
    case 'recent-searches': return 'Recent searches';
    case 'starred-lists': return 'Starred lists';
    default: return 'Page';
  }
}

export type TabKind =
  | 'home' | 'search' | 'drug' | 'compare' | 'drafts' | 'research' | 'cohort'
  | 'workstream' | 'report' | 'evidence' | 'sba' | 'fda_letter'
  | 'account' | 'other';

export function kindForPath(path: string): TabKind {
  const u = parse(path);
  if (u.pathname === '/') return u.search === '' ? 'home' : 'search';
  const seg = u.pathname.split('/').filter(Boolean);
  switch (seg[0]) {
    case 'drug': return 'drug';
    case 'compare-keyword': return 'compare';
    case 'manage-drafts': return 'drafts';
    case 'account':
    case 'subscriptions': return 'account';
    case 'research': return 'research';
    case 'cohort': return 'cohort';
    case 'workstream': return seg[2] === 'report' ? 'report' : 'workstream';
    case 'agents': return 'evidence';
    case 'sba': return 'sba';
    case 'fda-letter': return 'fda_letter';
    default: return 'other';
  }
}

function makeTab(path: string, now: number): Tab {
  return { id: newTabId(), path, title: titleForPath(path), lastActiveAt: now };
}

export function lruCandidate(s: TabsState): Tab | null {
  let best: Tab | null = null;
  for (const t of s.tabs) {
    if (t.id === s.activeTabId) continue;
    if (!best || t.lastActiveAt < best.lastActiveAt) best = t;
  }
  return best;
}

function addTab(s: TabsState, path: string, now: number, background: boolean): TabsState {
  const tab = makeTab(path, now);
  return {
    tabs: [...s.tabs, tab],
    activeTabId: background ? s.activeTabId : tab.id,
  };
}

export function focusTab(s: TabsState, id: string, now: number): TabsState {
  if (!s.tabs.some((t) => t.id === id)) return s;
  return {
    tabs: s.tabs.map((t) => (t.id === id ? { ...t, lastActiveAt: now } : t)),
    activeTabId: id,
  };
}

export function requestOpen(
  s: TabsState, path: string, now: number, opts?: { background?: boolean },
): OpenResult {
  path = canonicalTabPath(path);
  const background = opts?.background ?? false;
  const existing = s.tabs.find((t) => t.path === path);
  if (existing) {
    return { kind: 'focused', state: background ? s : focusTab(s, existing.id, now) };
  }
  if (s.tabs.length >= TAB_CAP) {
    const victim = lruCandidate(s);
    if (victim) {
      return {
        kind: 'needsEviction',
        pending: { path, evictId: victim.id, evictTitle: victim.title, background },
      };
    }
  }
  return { kind: 'opened', state: addTab(s, path, now, background) };
}

export function confirmOpen(s: TabsState, p: PendingOpen, now: number): TabsState {
  const kept: TabsState = {
    tabs: s.tabs.filter((t) => t.id !== p.evictId),
    activeTabId: s.activeTabId === p.evictId ? null : s.activeTabId,
  };
  return addTab(kept, p.path, now, p.background);
}

export function closeTab(s: TabsState, id: string): TabsState {
  const index = s.tabs.findIndex((t) => t.id === id);
  if (index < 0) return s;
  const tabs = s.tabs.filter((t) => t.id !== id);
  if (!tabs.length) return { tabs, activeTabId: null };
  if (s.activeTabId !== id) return { tabs, activeTabId: s.activeTabId };
  return { tabs, activeTabId: (tabs[index - 1] ?? tabs[0])!.id };
}

export function moveTab(
  s: TabsState, sourceId: string, targetId: string, position: 'before' | 'after',
): TabsState {
  if (sourceId === targetId) return s;
  const source = s.tabs.find((tab) => tab.id === sourceId);
  if (!source || !s.tabs.some((tab) => tab.id === targetId)) return s;

  const tabs = s.tabs.filter((tab) => tab.id !== sourceId);
  const targetIndex = tabs.findIndex((tab) => tab.id === targetId);
  tabs.splice(targetIndex + (position === 'after' ? 1 : 0), 0, source);
  if (tabs.every((tab, index) => tab === s.tabs[index])) return s;
  return { ...s, tabs };
}

/** D4/D5: path + title only; id, lastActiveAt and active tab are untouched. */
export function navigateTab(s: TabsState, id: string, path: string): TabsState {
  if (!s.tabs.some((t) => t.id === id)) return s;
  return {
    ...s,
    tabs: s.tabs.map((t) => (t.id === id ? { ...t, path, title: titleForPath(path) } : t)),
  };
}

/** A page-supplied title for a tab; path, id and focus are untouched. A no-op when unchanged. */
export function retitleTab(s: TabsState, id: string, title: string): TabsState {
  const t = s.tabs.find((x) => x.id === id);
  if (!t || t.title === title) return s;
  return { ...s, tabs: s.tabs.map((x) => (x.id === id ? { ...x, title } : x)) };
}

export function capabilityPath(a: CapabilityAction): string | null {
  const criterion = a.scope_draft?.criterion ?? '';
  switch (a.capability) {
    case 'cogni':
      return `/?mode=ai&q=${encodeURIComponent(criterion)}`;
    case 'keyword': {
      const request = buildKwRequest(
        baseFilters(),
        [{
          rows: [{
            keyword: criterion.trim(), section_scope: null,
            match_type: 'exact' as const, use_synonyms: false,
          }],
          inner_operator: 'AND' as const,
          proximity: null,
        }],
        ['label'],
        0,
      );
      return `/?mode=keyword&kwConfig=${encodeURIComponent(JSON.stringify(request))}`;
    }
    case 'investigator':
      // D13: a pinned cohort is Run-only; opening discovery would drop the pin.
      if ((a.scope_draft?.member_drug_ids ?? []).length > 0) return null;
      return `/?mode=researcher&rq=${encodeURIComponent(criterion)}`;
    default:
      return null;
  }
}

/** Pinned = non-empty member_drug_ids (same test as startCohortJob/capabilityPath). */
export function runTabPath(
  jobId: string,
  scope: { member_drug_ids?: number[] | null; criterion?: string | null },
): string {
  if ((scope.member_drug_ids ?? []).length > 0) {
    return `/cohort/${jobId}?c=${encodeURIComponent(scope.criterion ?? '')}`;
  }
  return `/research/${jobId}`;
}

export function parseRunTabPath(path: string): { jobId: string; pinned: boolean } | null {
  const seg = parse(path).pathname.split('/').filter(Boolean);
  if (seg.length < 2 || (seg[0] !== 'cohort' && seg[0] !== 'research')) return null;
  return { jobId: seg[1]!, pinned: seg[0] === 'cohort' };
}

export type TabPrefix = 'KW' | 'COGNI' | 'INV' | 'LABEL' | 'CMP';

/** R4.1: the mono prefix shown instead of the icon; null = keep the current icon / none. */
export function tabPrefix(path: string): TabPrefix | null {
  const u = parse(path);
  if (u.pathname === '/') {
    if (u.search === '') return null;
    const mode = u.searchParams.get('mode');
    if (mode === null || mode === 'keyword') return 'KW';
    if (mode === 'ai') return 'COGNI';
    if (mode === 'researcher') return 'INV';
    return null;
  }
  switch (u.pathname.split('/').filter(Boolean)[0]) {
    case 'research':
    case 'cohort': return 'INV';
    case 'drug': return 'LABEL';
    case 'compare-keyword': return 'CMP';
    default: return null;
  }
}

/** R4.2: display-time strip of the redundant leading kind word. */
export function displayTitle(title: string, prefixed: boolean): string {
  if (!prefixed) return title;
  for (const lead of ['Keyword: ', 'Cogni: ', 'Investigator: ']) {
    if (title.startsWith(lead)) return title.slice(lead.length);
  }
  return title;
}
